import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    employees: "1-10",
    interest: "Website Visitor ID",
    notes: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast({ title: "Missing fields", description: "Name, email and company are required." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/demos`, form);
      const apptUrl = res?.data?.appointmentUrl;
      toast({
        title: apptUrl ? "Request received — opening calendar…" : "Request received",
        description: apptUrl ? "Pick a time below." : "We'll be in touch within 1 business day.",
      });
      setForm({ name: "", email: "", company: "", role: "", employees: "1-10", interest: "Website Visitor ID", notes: "" });
      if (apptUrl) {
        // Pre-fill Google Appointment / Calendly with the lead's info when supported
        const sep = apptUrl.includes("?") ? "&" : "?";
        const target = `${apptUrl}${sep}name=${encodeURIComponent(form.name)}&email=${encodeURIComponent(form.email)}`;
        setTimeout(() => { window.location.href = target; }, 600);
      } else {
        setTimeout(() => navigate("/"), 900);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: "Could not submit", description: typeof msg === "string" ? msg : "Validation failed." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Book a Demo — BuyerBeacon Pro"
        description="See BuyerBeacon Pro in action. 30-minute walkthrough tailored to your industry and activation channels. Built for small business by 11x founders."
        path="/demo"
      />
      <Navbar />
      <main className="pt-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 py-14 lg:grid-cols-2 lg:px-8 lg:py-20">
          {/* left */}
          <div>
            <Link to="/" className="text-xs font-medium uppercase tracking-[0.14em] text-slate-500 hover:text-[#1e1b4b]">
              ← Back home
            </Link>
            <h1 className="mt-5 text-4xl font-medium tracking-tight text-[#1e1b4b] sm:text-5xl">
              Light up your <span className="text-slate-400">beacon</span>
            </h1>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-slate-600">
              Book a 20-minute walkthrough with a BuyerBeacon strategist. We'll
              tailor the demo to your industry, your ICP, and the channels you
              already use.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                "See live visitor identification on your own traffic",
                "Walk through Beacon Score against your top accounts",
                "Map activation channels across ads, CRM, and direct mail",
                "Small-business and agency plans available",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#d4af37]/15 text-[#d4af37]">
                    <Check className="h-3 w-3" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <Sparkles className="h-3.5 w-3.5 text-[#d4af37]" /> Trusted by
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-slate-400">
                <span>World Food Bank</span>
                <span>eSkrybe</span>
                <span>Serendipity Catering</span>
                <span>Garden Hero</span>
                <span>Times Future</span>
                <span>Trade Oracle Group</span>
              </div>
            </div>
          </div>

          {/* form */}
          <form
            onSubmit={submit}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_20px_60px_-30px_rgba(12,27,45,0.2)] sm:p-8"
          >
            <h2 className="text-lg font-semibold text-[#1e1b4b]">Request a demo</h2>
            <p className="mt-1 text-sm text-slate-500">Fill in your details and we'll reach out.</p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Full name" value={form.name} onChange={(v) => update("name", v)} />
              <Field label="Work email" type="email" value={form.email} onChange={(v) => update("email", v)} />
              <Field label="Company" value={form.company} onChange={(v) => update("company", v)} />
              <Field label="Role" value={form.role} onChange={(v) => update("role", v)} />
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-slate-600">Company size</label>
                <select
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#1e1b4b] outline-none focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#d4af37]/20"
                  value={form.employees}
                  onChange={(e) => update("employees", e.target.value)}
                >
                  {["1-10", "11-50", "51-200", "201-1000", "1000+"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-slate-600">Primary interest</label>
                <select
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#1e1b4b] outline-none focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#d4af37]/20"
                  value={form.interest}
                  onChange={(e) => update("interest", e.target.value)}
                >
                  {[
                    "Website Visitor ID",
                    "Consumer Data",
                    "Business Intelligence",
                    "Audience Builder",
                    "Offline Activation",
                    "Beacon Pulse",
                    "Buyer Intent",
                    "Authorized Partner",
                  ].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-1.5">
              <label className="text-xs font-medium text-slate-600">Anything we should know?</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#1e1b4b] outline-none focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#d4af37]/20"
                placeholder="Tell us about your stack, ICP, or goals…"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#1e1b4b] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1e1b4b]/92 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Request demo"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="mt-3 text-center text-[11px] text-slate-500">
              By submitting, you agree to our Privacy Policy. We never sell your data.
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#1e1b4b] outline-none transition-colors focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#d4af37]/20"
      />
    </div>
  );
}
